import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { Phone, User, Calendar, Clock, Filter, Download, Plus, Search, CheckCircle, XCircle } from 'lucide-react';

const CallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCall, setNewCall] = useState({
    lead_id: '',
    type: 'entrant',
    status: 'planifié',
    scheduled_at: '',
    duration: 0,
    notes: '',
    recording_url: ''
  });

  const { user } = useAuth();

  useEffect(() => {
    fetchCalls();
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('id, first_name, last_name, email, company')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setLeads(data || []);
    } catch (error) {
      console.error('Erreur chargement leads:', error);
    }
  };

  const fetchCalls = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('Utilisateur non connecté');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      
      let query = supabase
        .from('calls')
        .select(`
          *,
          lead:leads(*)
        `);
      
      // Si l'utilisateur est un agent, filtrer par agent_id
      if (profile?.role === 'agent') {
        console.log('Agent connecté, filtrage par agent_id:', user.id);
        query = query.eq('agent_id', user.id);
      }
      
      query = query.order('scheduled_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Erreur SQL:', error);
        throw error;
      }
      
      console.log('Appels chargés:', data?.length || 0);
      setCalls(data || []);
    } catch (error) {
      console.error('Erreur chargement appels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCall = async (e) => {
    e.preventDefault();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('calls')
        .insert([{
          ...newCall,
          agent_id: user?.id,
          created_by: user?.id
        }])
        .select();
      
      if (error) throw error;
      
      setCalls([...calls, ...data]);
      setShowAddModal(false);
      setNewCall({
        lead_id: '',
        type: 'entrant',
        status: 'planifié',
        scheduled_at: '',
        duration: 0,
        notes: '',
        recording_url: ''
      });
    } catch (error) {
      console.error('Erreur ajout appel:', error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('calls')
        .update({ status: newStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      setCalls(calls.map(call => 
        call.id === id ? { ...call, status: newStatus } : call
      ));
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  };

  const getLeadName = (lead) => {
    if (!lead) return 'Lead inconnu';
    return `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.email || 'Sans nom';
  };

  const getStatusColor = (status) => {
    const colors = {
      'planifié': 'bg-yellow-100 text-yellow-800',
      'effectué': 'bg-green-100 text-green-800',
      'manqué': 'bg-red-100 text-red-800',
      'reporté': 'bg-orange-100 text-orange-800',
      'annulé': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    return type === 'entrant' ? '↓' : '↑';
  };

  const filteredCalls = calls.filter(call => {
    const leadName = getLeadName(call.lead).toLowerCase();
    const matchesSearch = leadName.includes(searchTerm.toLowerCase()) ||
      call.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || call.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement des appels...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des Appels</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Planifier un appel
        </button>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="planifié">Planifié</option>
              <option value="effectué">Effectué</option>
              <option value="manqué">Manqué</option>
              <option value="reporté">Reporté</option>
              <option value="annulé">Annulé</option>
            </select>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter size={20} />
            Plus de filtres
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download size={20} />
            Exporter
          </button>
        </div>
      </div>

      {/* Tableau des appels */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lead
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date/Heure
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Durée
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agent
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCalls.map((call) => (
              <tr key={call.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {getLeadName(call.lead)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {call.lead?.company || ''}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-lg">
                    {getTypeIcon(call.type)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(call.scheduled_at).toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(call.scheduled_at).toLocaleTimeString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {call.duration ? `${call.duration} min` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={call.status}
                    onChange={(e) => handleStatusChange(call.id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(call.status)}`}
                  >
                    <option value="planifié">Planifié</option>
                    <option value="effectué">Effectué</option>
                    <option value="manqué">Manqué</option>
                    <option value="reporté">Reporté</option>
                    <option value="annulé">Annulé</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {call.agent_id === user?.id ? 'Moi' : 'Autre'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-blue-600 hover:text-blue-900 mr-3">
                    Détails
                  </button>
                  {call.recording_url && (
                    <button className="text-green-600 hover:text-green-900">
                      Écouter
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal d'ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-lg bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Planifier un appel</h3>
            <form onSubmit={handleAddCall}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Lead</label>
                  <select
                    required
                    value={newCall.lead_id}
                    onChange={(e) => setNewCall({...newCall, lead_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner un lead...</option>
                    {leads.map(lead => (
                      <option key={lead.id} value={lead.id}>
                        {lead.first_name} {lead.last_name} - {lead.company || lead.email}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type d'appel</label>
                  <select
                    value={newCall.type}
                    onChange={(e) => setNewCall({...newCall, type: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="entrant">Entrant</option>
                    <option value="sortant">Sortant</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date et heure</label>
                  <input
                    type="datetime-local"
                    required
                    value={newCall.scheduled_at}
                    onChange={(e) => setNewCall({...newCall, scheduled_at: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={newCall.notes}
                    onChange={(e) => setNewCall({...newCall, notes: e.target.value})}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Planifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsPage;
